import React, { useEffect, useState } from 'react';
import { Row, Col, Modal, Button, Form, Table } from 'react-bootstrap';
import DataTable from './TableLayout';
import baseURL from "../../../../Url/NodeBaseURL";
import axios from 'axios';
import { FaTrash, FaEdit, FaEye, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './RepairsTable.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import Swal from 'sweetalert2';
import InvoicePDFLayout from './RepairPDF';
import { v4 as uuidv4 } from 'uuid';

const RepairsTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [repairs, setRepairs] = useState([]);
  const [groupedRepairs, setGroupedRepairs] = useState([]);
  const { mobile } = location.state || {};
  const initialSearchValue = location.state?.mobile || '';
  const [permissions, setPermissions] = useState({});
  const [repairInvoiceDetails, setRepairInvoiceDetails] = useState({});

  // Get tab ID for localStorage
  const getTabId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    let tabId = urlParams.get('tabId');
    if (!tabId) {
      tabId = sessionStorage.getItem('tabId');
    }
    if (!tabId) {
      tabId = uuidv4();
      sessionStorage.setItem('tabId', tabId);
      const newUrl = `${window.location.pathname}?tabId=${tabId}`;
      window.history.replaceState({}, '', newUrl);
    }
    return tabId;
  };

  const tabId = getTabId();

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) return;
        const response = await fetch(`${baseURL}/permissions/${user.user_type_id}`);
        if (!response.ok) throw new Error('Failed to fetch permissions');
        const perms = await response.json();
        setPermissions(perms.permissions['Repairs'] || {});
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    fetchPermissions();
  }, []);

  useEffect(() => {
    if (mobile) {
      console.log("Selected Mobile from Dashboard:", mobile);
    }
  }, [mobile]);

  const fetchRepairs = async () => {
    try {
      const response = await axios.get(`${baseURL}/get/repairs`);
      const sorted = response.data.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      setRepairs(sorted);
      groupRepairsByRepairNo(sorted);
      console.log("Sorted Repairs =", sorted);
    } catch (error) {
      console.error("Error fetching repairs:", error);
    }
  };

  const groupRepairsByRepairNo = (repairsData) => {
    const grouped = repairsData.reduce((acc, repair) => {
      if (!acc[repair.repair_no]) {
        acc[repair.repair_no] = {
          repair_no: repair.repair_no,
          date: repair.date,
          account_name: repair.account_name,
          mobile: repair.mobile,
          entry_type: repair.entry_type,
          status: repair.status,
          invoice: repair.invoice,
          invoice_number: repair.invoice_number,
          taxable_amt: repair.taxable_amt,
          net_bill_amt: repair.net_bill_amt,
          items: [],
          total_taxable_amt: 0,
          total_net_bill_amt: 0
        };
      }

      acc[repair.repair_no].items.push(repair);
      // acc[repair.repair_no].total_taxable_amt += parseFloat(repair.total_amt || 0);
      // acc[repair.repair_no].total_net_bill_amt += parseFloat(repair.net_bill_amt || 0);

      return acc;
    }, {});

    setGroupedRepairs(Object.values(grouped));
  };

  useEffect(() => {
    fetchRepairs();
  }, []);

  // const handleRepairEdit = (id) => {
  //   navigate(`/repairs/${id}`);
  // };

  const handleRepairEdit = async (repairGroup) => {
    try {
      // Get all repairs with the same repair_no
      const response = await axios.get(`${baseURL}/get/repairs-by-repairno/${repairGroup.repair_no}`);
      const repairItems = response.data;

      if (repairItems && repairItems.length > 0) {
        // Store all repair items in localStorage
        localStorage.setItem(`repairItems_${tabId}`, JSON.stringify(repairItems));

        // Get the first item to extract customer details
        const firstItem = repairItems[0];

        // Navigate to repair form with customer data and repair number
        navigate(`/repairs`, {
          state: {
            customerData: {
              customer_id: firstItem.customer_id,
              account_name: firstItem.account_name,
              mobile: firstItem.mobile,
              email: firstItem.email,
              address1: firstItem.address1,
              address2: firstItem.address2,
              city: firstItem.city
            },
            repair_no: repairGroup.repair_no
          }
        });
      } else {
        alert('No repair items found for this repair number');
      }
    } catch (error) {
      console.error('Error fetching repair items:', error);
      alert('Failed to load repair items for editing');
    }
  };


  const handleDeleteRepair = async (repair) => {
    if (!window.confirm(`Are you sure you want to delete repair #${repair.repair_no}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${baseURL}/repairs`, {
        data: { repair_no: repair.repair_no }
      });

      if (response.status === 200) {
        alert('Repair entry deleted successfully!');
        // Refresh the repairs list
        fetchRepairs();
      }
    } catch (error) {
      console.error('Error deleting repair:', error);
      alert('Failed to delete repair entry');
    }
  };

  const columns = React.useMemo(() => {
    const cols = [
      {
        Header: 'SNo.',
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: 'Date',
        accessor: 'date',
        Cell: ({ value }) => {
          if (!value) return "";
          const date = new Date(value);
          return date.toLocaleDateString('en-GB');
        },
      },
      { Header: 'Repair No', accessor: 'repair_no' },
      { Header: 'Customer', accessor: 'account_name' },
      { Header: 'Mobile', accessor: 'mobile' },
      { Header: 'Entry Type', accessor: 'entry_type' },
      {
        Header: 'Taxable Amt',
        accessor: 'taxable_amt',
        Cell: ({ value }) => value ? parseFloat(value).toFixed(2) : '0.00',
      },
      {
        Header: 'Total Amt',
        accessor: 'net_bill_amt',
        Cell: ({ value }) => value ? parseFloat(value).toFixed(2) : '0.00',
      },
      { Header: 'Status', accessor: 'status' },
      {
        Header: 'Invoice',
        accessor: 'convert',
        Cell: ({ row }) => {
          const isDelivery = row.original.status === 'Pending';
          const isConverted = row.original.invoice === 'Converted';
          const isEnabled = isDelivery && !isConverted;

          if (isConverted && row.original.invoice_number) {
            return (
              <a
                href={`${baseURL}/invoices/${row.original.invoice_number}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: '#007bff',
                  fontWeight: 'bold',
                  fontSize: '0.800rem'
                }}
              >
                📝 View
              </a>
            );
          }
          return (
            <Button
              style={{
                backgroundColor: isEnabled ? '#28a745' : '#ccc',
                borderColor: isEnabled ? '#28a745' : '#ccc',
                fontSize: '0.800rem',
                padding: '0.10rem 0.5rem',
                cursor: isEnabled ? 'pointer' : 'not-allowed',
              }}
              disabled={!isEnabled}
              onClick={() => handleConvert(row.original)}
            >
              Generate
            </Button>
          );
        },
      }
    ];

    if (permissions.view || permissions.modify || permissions.delete) {
      cols.push({
        Header: 'ACTION',
        Cell: ({ row }) => {
          const isDelivery = row.original.status === 'Delivered to Customer';
          const isConverted = row.original.invoice === 'Converted';
          const canEdit = permissions.modify && !isDelivery && !isConverted;
          const canDelete = permissions.delete && !isDelivery && !isConverted;

          return (
            <div className="d-flex align-items-center">
              {permissions.modify && (
                // In the ACTION column Cell renderer, update the FaEdit onClick:
                <FaEdit
                  style={{
                    cursor: canEdit ? 'pointer' : 'not-allowed',
                    marginLeft: '10px',
                    color: canEdit ? 'blue' : 'gray',
                    opacity: canEdit ? 1 : 0.5
                  }}
                  onClick={canEdit ? () => handleRepairEdit(row.original) : undefined}
                />
              )}
              {/* {permissions.delete && (
                <FaTrash
                  style={{
                    cursor: canDelete ? 'pointer' : 'not-allowed',
                    marginLeft: '10px',
                    color: canDelete ? 'red' : 'gray',
                    opacity: canDelete ? 1 : 0.5
                  }}
                  onClick={canDelete ? () => handleDeleteRepair(row.original) : undefined}
                />
              )} */}
              {permissions.delete && (
                <FaTrash
                  style={{
                    cursor: 'pointer',
                    marginLeft: '10px',
                    color: 'red',
                    opacity:1
                  }}
                  onClick={() => handleDeleteRepair(row.original)}
                />
              )}
            </div>
          );
        },
      });
    }

    return cols;
  }, [groupedRepairs, permissions]);

  const handleConvert = async (repairGroup) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Do you want to convert repair ${repairGroup.repair_no} with ${repairGroup.items.length} items to invoice?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, convert it!',
        cancelButtonText: 'No, cancel',
      });

      if (result.isConfirmed) {
        // Convert all items in the repair group with group totals
        const convertPromises = repairGroup.items.map(repair =>
          axios.post(`${baseURL}/convert-repair`, {
            ...repair,
            repairGroup: repairGroup // Pass the entire group for totals calculation
          })
        );

        const results = await Promise.all(convertPromises);

        // Check if all conversions were successful
        const allSuccess = results.every(result => result.data.success);

        if (allSuccess) {
          Swal.fire('Converted!', `Repair ${repairGroup.repair_no} has been converted to invoice with ${repairGroup.items.length} items.`, 'success');

          // Generate PDF for the entire repair group
          const invoiceResponse = await axios.get(`${baseURL}/get-repair-invoice-group/${repairGroup.repair_no}`);
          if (invoiceResponse.data.success) {
            const invoiceData = invoiceResponse.data.invoice;
            console.log("Invoice details=", invoiceData);
            const invoiceNumber = invoiceData.invoice_number;

            setRepairInvoiceDetails(invoiceData);
            const pdfDoc = (
              <InvoicePDFLayout
                repairDetails={invoiceData}
                sellerName="Sri Mahagnapathi Jewellers"
              />
            );
            const pdfBlob = await pdf(pdfDoc).toBlob();
            await handleSavePDFToServer(pdfBlob, invoiceNumber);
          }
          fetchRepairs();
        } else {
          throw new Error('Some items failed to convert');
        }
      }
      // navigate('/salestable');
    } catch (error) {
      console.error('Error generating invoice:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An unexpected error occurred while converting repair to invoice!',
        confirmButtonColor: '#d33',
      });
    }
  };

  const handleSavePDFToServer = async (pdfBlob, invoiceNumber) => {
    const formData = new FormData();
    formData.append("invoice", pdfBlob, `${invoiceNumber}.pdf`);

    try {
      const response = await fetch(`${baseURL}/upload-invoice`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload invoice");
      }

      console.log(`Invoice ${invoiceNumber} saved on server`);
    } catch (error) {
      console.error("Error uploading invoice:", error);
    }
  };

  // Expanded row component for showing individual items
  const renderRowSubComponent = React.useCallback(
    ({ row }) => {
      const items = row.original.items || [];

      return (
        <div className="expanded-row-container">
          <table className="table table-bordered table-sm expanded-table">
            <thead style={{ fontSize: "13px" }}>
              <tr className="table-secondary">
                <th>S No</th>
                <th>Particulars</th>
                <th>Gross / Net Wt</th>
                <th>Amount</th>
                <th>Image</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "13px" }}>
              {items.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.category}</td>
                  <td>{item.gross_weight || '0.00'}</td>
                  <td>{item.estimated_amt ? parseFloat(item.estimated_amt).toFixed(2) : '0.00'}</td>
                  <td>
                    {item.image ? (
                      <img
                        src={item.image}
                        alt="Repair Image"
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                          borderRadius: "5px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const newWindow = window.open();
                          newWindow.document.write(
                            `<img src="${item.image}" alt="Repair Image" style="width: 100%; height: auto;" />`
                          );
                        }}
                        onError={(e) => e.target.src = "/placeholder.png"}
                      />
                    ) : (
                      "No Image"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    },
    [permissions]
  );

  return (
    <div className="main-container">
      <div className="repairs-table-container">
        <Row className="mb-4">
          <Col className="d-flex justify-content-between align-items-center">
            <h3>Repairs</h3>
            {permissions.add && (
              <Button
                className="create_but"
                onClick={() => navigate('/repairs')}
                style={{ backgroundColor: '#a36e29', borderColor: '#a36e29' }}
              >
                + Create
              </Button>
            )}
          </Col>
        </Row>
        <DataTable
          columns={columns}
          data={groupedRepairs}
          initialSearchValue={initialSearchValue}
          renderRowSubComponent={renderRowSubComponent}
          expandAll={true}
        />
      </div>
    </div>
  );
};

export default RepairsTable;